<template>
  <v-dialog v-model="internalShow" max-width="1200" scrollable content-class="scorecard-print-dialog">
    <v-card>
      <!-- Header -->
      <v-card-title class="pa-4 bg-gray-50 border-b d-flex justify-space-between align-center no-print">
        <h2 class="text-h5 font-weight-bold d-flex align-center" style="color: #2e7d32; margin: 0;">
          <v-icon color="success" class="mr-2">mdi-golf</v-icon>
          Registro Manual de Scorecard
        </h2>
        <div>
          <v-btn text @click="internalShow = false" class="mr-2">Cancelar</v-btn>
          <v-btn outlined color="info" @click="printScorecard" class="mr-2">
            <v-icon left>mdi-printer</v-icon> Imprimir
          </v-btn>
          <v-btn color="success" @click="saveScorecard">
            <v-icon left>mdi-content-save</v-icon> Guardar Scorecard
          </v-btn>
        </div>
      </v-card-title>

      <!-- Contenido scrolleable -->
      <v-card-text class="pa-0">
        <!-- Player Details Form -->
        <div class="pa-4 no-print" style="border-bottom: 1px solid #e0e0e0; background-color: #fff;">
          <v-row dense>
            <!-- 1. Reservación Primero -->
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="form.reservation"
                :items="reservations"
                item-text="player_names"
                item-value="reservation_id"
                label="Reservación"
                outlined
                dense
                hide-details
                clearable
                placeholder="Buscar reservación..."
              >
                <template v-slot:selection="{ item }">
                  {{ formatDate(item.date_played) }} - {{ item.player_names }}
                </template>
                <template v-slot:item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">{{ item.player_names || 'Sin nombre' }}</v-list-item-title>
                    <v-list-item-subtitle>{{ formatDate(item.date_played) }} - {{ item.course || 'Campo' }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- 2. Jugador (limitado a la reservación) -->
            <v-col cols="12" md="3">
              <v-combobox
                v-model="form.user"
                :items="reservationPlayerNames"
                label="Nombre del jugador"
                outlined
                dense
                hide-details
                clearable
                :loading="loadingUsers"
                placeholder="Seleccionar jugador"
                :disabled="!form.reservation"
              >
              </v-combobox>
            </v-col>

            <v-col cols="12" sm="6" md="2">
              <v-text-field type="date" v-model="form.date" label="Fecha" outlined dense hide-details></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="1">
              <v-text-field
                type="number"
                min="0"
                max="54"
                v-model.number="form.handicap"
                label="HCP"
                outlined
                dense
                hide-details
                @blur="validateHandicap"
                :error="handicapError"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select v-model="form.tee" :items="tees" item-text="name" item-value="id" label="Tee Jugado" outlined dense hide-details></v-select>
            </v-col>
            <v-col cols="12" sm="6" md="2">
              <v-select v-model="form.startingHole" :items="holeOptions" label="Hoyo de inicio" outlined dense hide-details></v-select>
            </v-col>
          </v-row>
        </div>

        <!-- Print Header -->
        <div class="d-none print-block pa-4" style="border-bottom: 2px solid #2e7d32; margin-bottom: 16px;">
          <h1 style="text-align: center; color: #2e7d32; margin-bottom: 16px; text-transform: uppercase; font-size: 24px;">Scorecard Oficial</h1>
          <table style="width: 100%; font-size: 14px;">
            <tr>
              <td><strong>Jugador:</strong> {{ displayPlayerName || '____________________' }}</td>
              <td style="text-align: right;"><strong>Fecha:</strong> {{ form.date }}</td>
            </tr>
            <tr>
              <td><strong>Handicap:</strong> {{ form.handicap }}</td>
              <td style="text-align: right;"><strong>Tee:</strong> {{ displayTeeName }} | <strong>Inicio:</strong> {{ form.startingHole }}</td>
            </tr>
          </table>
        </div>

        <!-- Scorecard Tables -->
        <div class="pa-4">
          <v-row>
            <!-- Front 9 -->
            <v-col cols="12" lg="6">
              <div class="d-flex justify-space-between align-end pb-2 mb-3" style="border-bottom: 2px solid #388e3c;">
                <h3 class="font-weight-bold" style="color: #2e7d32; margin: 0;">Primera Vuelta (Hoyos 1 - 9)</h3>
                <span class="caption grey--text no-print">Par {{ frontNinePar }}</span>
              </div>
              <v-simple-table dense class="scorecard-table elevation-1">
                <template v-slot:default>
                  <thead>
                    <tr style="background-color: #f5f5f5;">
                      <th class="text-center font-weight-bold px-1" style="width: 40px;">Hoyo</th>
                      <th class="text-center font-weight-bold px-1" style="width: 70px;">
                        <div style="line-height:1.2">Ventaja<br><span style="font-size:10px;font-weight:normal;color:#757575;">Cab/Dam</span></div>
                      </th>
                      <th class="text-center font-weight-bold px-1" style="width: 40px; background-color: #e0e0e0;">Par</th>
                      <th class="text-center font-weight-bold px-1" style="width: 80px;">Golpes</th>
                      <th class="text-center font-weight-bold px-1" style="width: 50px;">+/-</th>
                      <th class="text-center font-weight-bold px-1" style="width: 110px;">Etiqueta</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="h in frontNine" :key="'front-'+h.hole_number">
                      <td class="text-center font-weight-bold">{{ h.hole_number }}</td>
                      <td class="text-center caption grey--text">
                        {{ h.advMen }} <span class="mx-1 font-weight-light">|</span> {{ h.advWomen }}
                      </td>
                      <td class="text-center font-weight-bold" style="background-color: #fafafa;">{{ h.par }}</td>
                      <td class="text-center px-1">
                        <input
                          type="number"
                          min="1"
                          max="99"
                          v-model.number="h.strokes"
                          @input="limitStrokes(h)"
                          class="stroke-input no-print"
                          placeholder="-"
                        />
                        <span class="d-none print-inline font-weight-bold" style="font-size: 16px;">{{ h.strokes || '' }}</span>
                      </td>
                      <td class="text-center font-weight-bold" :class="getScoreColorClass(getScoreVsPar(h.strokes, h.par))">
                        {{ formatScore(getScoreVsPar(h.strokes, h.par)) }}
                      </td>
                      <td class="text-center px-1">
                        <div v-if="h.strokes" class="score-label" :style="getLabelStyles(getScoreVsPar(h.strokes, h.par))">
                          {{ getLabel(getScoreVsPar(h.strokes, h.par)) }}
                        </div>
                      </td>
                    </tr>
                    <tr style="background-color: #f5f5f5; font-weight: bold; border-top: 2px solid #bdbdbd;">
                      <td colspan="2" class="text-right text-uppercase grey--text text--darken-2">Out</td>
                      <td class="text-center">{{ frontNinePar }}</td>
                      <td class="text-center text-h6 success--text text--darken-2">{{ frontNineStrokes || '-' }}</td>
                      <td colspan="2"></td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>

            <!-- Back 9 -->
            <v-col cols="12" lg="6">
              <div class="d-flex justify-space-between align-end pb-2 mb-3" style="border-bottom: 2px solid #388e3c;">
                <h3 class="font-weight-bold" style="color: #2e7d32; margin: 0;">Segunda Vuelta (Hoyos 10 - 18)</h3>
                <span class="caption grey--text no-print">Par {{ backNinePar }}</span>
              </div>
              <v-simple-table dense class="scorecard-table elevation-1">
                <template v-slot:default>
                  <thead>
                    <tr style="background-color: #f5f5f5;">
                      <th class="text-center font-weight-bold px-1" style="width: 40px;">Hoyo</th>
                      <th class="text-center font-weight-bold px-1" style="width: 70px;">
                        <div style="line-height:1.2">Ventaja<br><span style="font-size:10px;font-weight:normal;color:#757575;">Cab/Dam</span></div>
                      </th>
                      <th class="text-center font-weight-bold px-1" style="width: 40px; background-color: #e0e0e0;">Par</th>
                      <th class="text-center font-weight-bold px-1" style="width: 80px;">Golpes</th>
                      <th class="text-center font-weight-bold px-1" style="width: 50px;">+/-</th>
                      <th class="text-center font-weight-bold px-1" style="width: 110px;">Etiqueta</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="h in backNine" :key="'back-'+h.hole_number">
                      <td class="text-center font-weight-bold">{{ h.hole_number }}</td>
                      <td class="text-center caption grey--text">
                        {{ h.advMen }} <span class="mx-1 font-weight-light">|</span> {{ h.advWomen }}
                      </td>
                      <td class="text-center font-weight-bold" style="background-color: #fafafa;">{{ h.par }}</td>
                      <td class="text-center px-1">
                        <input
                          type="number"
                          min="1"
                          max="99"
                          v-model.number="h.strokes"
                          @input="limitStrokes(h)"
                          class="stroke-input no-print"
                          placeholder="-"
                        />
                        <span class="d-none print-inline font-weight-bold" style="font-size: 16px;">{{ h.strokes || '' }}</span>
                      </td>
                      <td class="text-center font-weight-bold" :class="getScoreColorClass(getScoreVsPar(h.strokes, h.par))">
                        {{ formatScore(getScoreVsPar(h.strokes, h.par)) }}
                      </td>
                      <td class="text-center px-1">
                        <div v-if="h.strokes" class="score-label" :style="getLabelStyles(getScoreVsPar(h.strokes, h.par))">
                          {{ getLabel(getScoreVsPar(h.strokes, h.par)) }}
                        </div>
                      </td>
                    </tr>
                    <tr style="background-color: #f5f5f5; font-weight: bold; border-top: 2px solid #bdbdbd;">
                      <td colspan="2" class="text-right text-uppercase grey--text text--darken-2">In</td>
                      <td class="text-center">{{ backNinePar }}</td>
                      <td class="text-center text-h6 success--text text--darken-2">{{ backNineStrokes || '-' }}</td>
                      <td colspan="2"></td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
        </div>

        <!-- Footer Summary Bar -->
        <div class="summary-footer">
          <v-row class="ma-0 text-center" align="center">
            <v-col cols="6" sm="3" class="summary-col">
              <div class="summary-title">Par Salida / Jugado</div>
              <div class="summary-value">{{ playedParTotal }} <span class="summary-sub">/ 72</span></div>
            </v-col>
            <v-col cols="6" sm="3" class="summary-col">
              <div class="summary-title">Golpes Totales</div>
              <div class="summary-value">{{ totalGross || '-' }}</div>
            </v-col>
            <v-col cols="6" sm="3" class="summary-col">
              <div class="summary-title">Neto <span style="font-size: 10px; font-weight: normal; text-transform: none;">(Gross - HCP {{ effectiveHandicap }})</span></div>
              <div class="summary-value success--text">{{ netScore || '-' }}</div>
            </v-col>
            <v-col cols="6" sm="3">
              <div class="summary-title">Total vs Par</div>
              <div class="summary-value" :class="totalVsParColorClass">
                {{ holesPlayedCount > 0 ? formatScore(totalVsPar) : '-' }}
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { apiCall } from '@/utils/golfScorecards'
import { mapActions } from 'vuex'

export default {
  name: 'ManualScorecardModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    defaultPlayerName: {
      type: String,
      default: ''
    },
    defaultHandicap: {
      type: Number,
      default: 0
    },
    reservations: {
      type: Array,
      default: () => []
    },
    tees: {
      type: Array,
      default: () => []
    }
  },
  data () {
    const pars = [4, 3, 4, 4, 4, 4, 5, 3, 5, 5, 3, 4, 4, 4, 3, 4, 4, 5]
    const advMen = [3, 13, 15, 7, 5, 1, 17, 11, 9, 4, 12, 6, 14, 18, 8, 2, 16, 10]
    const advWomen = [7, 15, 17, 9, 5, 1, 11, 13, 3, 2, 10, 14, 8, 18, 12, 4, 16, 6]

    const holesData = Array.from({ length: 18 }, (_, i) => ({
      hole_number: i + 1,
      par: pars[i],
      strokes: null,
      advMen: advMen[i],
      advWomen: advWomen[i]
    }))

    return {
      form: {
        user: this.defaultPlayerName,
        date: new Date().toISOString().slice(0, 10),
        handicap: this.defaultHandicap,
        reservation: null,
        tee: null,
        startingHole: 'Hole 1'
      },
      holeOptions: ['Hole 1', 'Hole 10'],
      holes: holesData,
      users: [],
      loadingUsers: false,
      handicapError: false
    }
  },
  computed: {
    internalShow: {
      get () {
        return this.show
      },
      set (value) {
        this.$emit('update:show', value)
      }
    },
    frontNine () {
      return this.holes.slice(0, 9)
    },
    backNine () {
      return this.holes.slice(9, 18)
    },
    frontNinePar () {
      return this.frontNine.reduce((acc, h) => acc + h.par, 0)
    },
    backNinePar () {
      return this.backNine.reduce((acc, h) => acc + h.par, 0)
    },
    frontNineStrokes () {
      return this.frontNine.reduce((acc, h) => acc + (h.strokes || 0), 0)
    },
    backNineStrokes () {
      return this.backNine.reduce((acc, h) => acc + (h.strokes || 0), 0)
    },
    totalGross () {
      return this.frontNineStrokes + this.backNineStrokes
    },
    holesPlayedCount () {
      const validStrokes = this.holes.filter(h => typeof h.strokes === 'number' && h.strokes > 0)
      return validStrokes.length
    },
    effectiveHandicap () {
      if (this.holesPlayedCount > 9) {
        return this.form.handicap
      } else if (this.holesPlayedCount > 0) {
        return Math.ceil(this.form.handicap / 2)
      }
      return 0
    },
    netScore () {
      if (this.holesPlayedCount === 0) return 0
      return this.totalGross - this.effectiveHandicap
    },
    playedParTotal () {
      return this.holes.reduce((acc, h) => acc + (h.strokes > 0 ? h.par : 0), 0)
    },
    totalVsPar () {
      if (this.holesPlayedCount === 0) return 0
      return this.totalGross - this.playedParTotal
    },
    totalVsParColorClass () {
      if (this.holesPlayedCount === 0) return 'grey--text'
      if (this.totalVsPar < 0) return 'info--text text-blue-400' // Under par
      if (this.totalVsPar === 0) return 'success--text text-green-400' // Even par
      return 'error--text text-red-400' // Over par
    },
    reservationPlayerNames () {
      if (!this.form.reservation) return []
      const res = this.reservations.find(r => r.reservation_id === this.form.reservation)
      if (!res || !res.player_names) return []
      return res.player_names.split(',').map(name => name.trim()).filter(Boolean)
    },
    displayPlayerName () {
      return this.form.user || ''
    },
    displayTeeName () {
      const teeObj = this.tees.find(t => t.id === this.form.tee)
      return teeObj ? teeObj.name : ''
    }
  },
  watch: {
    'form.reservation' (newVal) {
      if (newVal) {
        const res = this.reservations.find(r => r.reservation_id === newVal)
        if (res && res.date_played) {
          this.form.date = res.date_played.slice(0, 10)
        }
        // Auto-seleccionar primer jugador de la lista si el actual no está en la reservación
        this.$nextTick(() => {
          if (this.reservationPlayerNames.length > 0 && !this.reservationPlayerNames.includes(this.form.user)) {
            this.form.user = this.reservationPlayerNames[0]
          }
        })
      }
    },
    'form.user' (newVal) {
      if (newVal) {
        this.loadUserHandicap(newVal)
      }
    },
    defaultPlayerName (val) {
      if (!this.form.user) this.form.user = val
    },
    defaultHandicap (val) {
      if (this.form.handicap === 0) this.form.handicap = val
    },
    show (isVisible) {
      if (isVisible) {
        if (!this.form.user && this.defaultPlayerName) {
          this.form.user = this.defaultPlayerName
        }
        if (this.users.length === 0) {
          this.loadUsers()
        }
      }
    }
  },
  methods: {
    ...mapActions('handicap', ['fetchUserHandicap']),
    async loadUsers () {
      this.loadingUsers = true
      try {
        const response = await apiCall('get', '/residents/all')
        const residents = response.data.residents || response.data || []
        this.users = (Array.isArray(residents) ? residents : []).map(resident => {
          if (resident.idUser) {
            return {
              id: resident.idUser.id,
              full_name: `${resident.idUser.first_name || ''} ${resident.idUser.last_name || ''}`.trim(),
              email: resident.idUser.email
            }
          }
          return resident
        }).filter(u => u && u.id)
      } catch (err) {
        console.error('Error fetching users:', err)
      } finally {
        this.loadingUsers = false
      }
    },
    async loadUserHandicap (userName) {
      // Buscar el usuario en la lista local
      const foundUser = this.users.find(u => u.full_name.toLowerCase() === userName.toLowerCase())
      if (foundUser) {
        try {
          const handicap = await this.fetchUserHandicap(foundUser.id)
          if (handicap) {
            this.form.handicap = handicap.official_handicap
          }
        } catch (err) {
          console.error('Error fetching handicap:', err)
        }
      }
    },
    formatDate (date) {
      if (!date) return '--'
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    limitStrokes (hole) {
      if (hole.strokes !== null && hole.strokes !== '') {
        const parsed = parseInt(hole.strokes)
        if (isNaN(parsed)) {
          hole.strokes = null
          return
        }
        let s = Math.abs(parsed)
        if (s > 99) s = Math.floor(s / 10)
        if (s > 99) s = 99

        // This makes Vue 2 reactivity work properly if set directly
        hole.strokes = s === 0 ? null : s
      }
    },
    validateHandicap () {
      const hcp = this.form.handicap
      const isValid = typeof hcp === 'number' && hcp >= 0 && hcp <= 54
      this.handicapError = !isValid
      if (!isValid && hcp !== null && hcp !== '') {
        this.form.handicap = Math.max(0, Math.min(54, hcp))
      }
    },
    getScoreVsPar (strokes, par) {
      if (!strokes || isNaN(strokes)) return null
      return strokes - par
    },
    formatScore (scoreVsPar) {
      if (scoreVsPar === null) return ''
      return scoreVsPar > 0 ? `+${scoreVsPar}` : `${scoreVsPar}`
    },
    getLabel (scoreVsPar) {
      if (scoreVsPar === null) return ''
      if (scoreVsPar <= -2) return 'Eagle'
      if (scoreVsPar === -1) return 'Birdie'
      if (scoreVsPar === 0) return 'Par'
      if (scoreVsPar === 1) return 'Bogey'
      if (scoreVsPar === 2) return 'Doble Bogey'
      if (scoreVsPar === 3) return 'Triple Bogey'
      return this.formatScore(scoreVsPar)
    },
    getScoreColorClass (scoreVsPar) {
      if (scoreVsPar === null) return ''
      if (scoreVsPar < 0) return 'info--text'
      if (scoreVsPar === 0) return 'success--text'
      return 'error--text'
    },
    getLabelStyles (scoreVsPar) {
      if (scoreVsPar === null) return {}
      if (scoreVsPar <= -2) return { backgroundColor: '#fff9c4', color: '#f57f17', border: '1px solid #fbc02d' } // Eagle
      if (scoreVsPar === -1) return { backgroundColor: '#e8f5e9', color: '#2e7d32', border: '1px solid #81c784' } // Birdie
      if (scoreVsPar === 0) return { backgroundColor: '#e3f2fd', color: '#1565c0', border: '1px solid #64b5f6' } // Par
      if (scoreVsPar === 1) return { backgroundColor: '#fff3e0', color: '#e65100', border: '1px solid #ffb74d' } // Bogey
      if (scoreVsPar >= 2) return { backgroundColor: '#ffebee', color: '#c62828', border: '1px solid #e57373' } // DB or worse
      return {}
    },
    printScorecard () {
      setTimeout(() => {
        window.print()
      }, 500)
    },
    saveScorecard () {
      let playerName = typeof this.form.user === 'string' ? this.form.user : (this.form.user?.full_name || '')
      let userId = null

      // Mapear automáticamente el UUID del usuario si está en nuestro catálogo global
      const foundUser = this.users.find(u => u.full_name.toLowerCase() === playerName.toLowerCase())
      if (foundUser) {
        userId = foundUser.id
      }

      const payload = {
        reservation: this.form.reservation,
        player_name: playerName,
        user: userId, // UUID mapeado silenciosamente si es residente
        handicap: this.form.handicap,
        tee: this.form.tee,
        date: this.form.date,
        starting_hole: this.form.startingHole,
        holes: this.holes
          .filter(h => h.strokes !== null && h.strokes !== '')
          .map(h => ({
            hole_number: h.hole_number,
            strokes: h.strokes
          }))
      }
      this.$emit('save', payload)
      this.internalShow = false
    }
  }
}
</script>

<style scoped>
.stroke-input {
  width: 100%;
  height: 36px;
  text-align: center;
  font-weight: bold;
  font-size: 16px;
  border: 1px solid transparent;
  border-radius: 4px;
  outline: none;
  background-color: transparent;
  transition: all 0.2s;
}
.stroke-input:hover {
  border-color: #e0e0e0;
}
.stroke-input:focus {
  border-color: #4caf50;
  box-shadow: 0 0 0 1px #4caf50;
}
.score-label {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  text-transform: uppercase;
  white-space: nowrap;
  display: inline-block;
  min-width: 70px;
  text-align: center;
}

.summary-footer {
  margin: 16px;
  background-color: #212121;
  color: white;
  border-radius: 8px;
  padding: 16px;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.5);
}
.summary-col {
  border-right: 1px solid #424242;
}
.summary-col:last-child {
  border-right: none;
}
.summary-title {
  font-size: 11px;
  color: #9e9e9e;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}
.summary-value {
  font-size: 28px;
  font-weight: 900;
}
.summary-sub {
  font-size: 14px;
  font-weight: normal;
  color: #757575;
}

</style>

<style>
@media print {
  /* Ocultar barra de navegacion, headers y todo lo que no sea el dialog-content */
  body * {
    visibility: hidden;
  }

  /* Mantener el div base del dialog visible */
  .v-dialog--active, .v-dialog--active * {
    visibility: visible;
  }

  /* Reiniciar la posición de los componentes de vuetify y dialog */
  .v-dialog__content {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    padding: 0 !important;
    margin: 0 !important;
    width: 100% !important;
    height: 100% !important;
    display: block !important;
    align-items: flex-start !important;
    justify-content: flex-start !important;
  }

  .v-dialog {
    box-shadow: none !important;
    margin: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
  }

  .v-card {
    box-shadow: none !important;
  }

  /* Ocultar la capa gris oscuro/overlay del dialog */
  .v-overlay {
    display: none !important;
  }

  .no-print {
    display: none !important;
  }

  .print-block {
    display: block !important;
  }

  .print-inline {
    display: inline !important;
  }

  .summary-footer {
    background-color: transparent !important;
    color: black !important;
    border: 2px solid #2e7d32 !important;
    box-shadow: none !important;
    margin: 0 !important;
    border-radius: 0 !important;
  }
  .summary-title {
    color: #424242 !important;
  }
  .summary-col {
    border-right: 1px solid #e0e0e0 !important;
  }

  /* Forzar colores en Chrome/Edge */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Evitar saltos de página dentro de la tabla */
  .scorecard-table {
    page-break-inside: avoid;
  }
}
</style>
